import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Building2,
  MapPin,
  Gauge,
  FileCheck2,
  Zap,
  ClipboardList,
  Shield,
  Factory,
  Plug,
} from "lucide-react";
import clsx from "clsx";
import type { ResearchReportData } from "./types";

const COMPLEXITY_COLOR: Record<string, string> = {
  low: "text-green-600 bg-green-50",
  medium: "text-amber-600 bg-amber-50",
  high: "text-red-600 bg-red-50",
};

export const ProjectOverviewSidebar = ({
  report,
}: {
  report: ResearchReportData;
}) => {
  const { t } = useTranslation();

  const completenessPercent = report.document_completeness
    ? Math.round(report.document_completeness * 100)
    : null;

  const statItems = useMemo(
    () =>
      [
        {
          icon: Zap,
          label: t("smartSurveyor.researchReport.fields.circuits"),
          value: report.estimated_total_circuits
            ? `~${report.estimated_total_circuits}`
            : null,
          color: "text-amber-600 bg-amber-50",
        },
        {
          icon: ClipboardList,
          label: t("smartSurveyor.researchReport.fields.boqPositions"),
          value: report.boq_total_positions
            ? `${report.boq_total_positions}`
            : null,
          color: "text-blue-600 bg-blue-50",
        },
      ].filter((s) => s.value),
    [report.estimated_total_circuits, report.boq_total_positions, t],
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-md bg-gray-900 flex items-center justify-center">
            <Building2 size={14} className="text-white" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">
            {t("smartSurveyor.researchReport.projectOverview")}
          </h3>
        </div>

        {/* Project name & type */}
        {report.project_name && (
          <div className="text-sm font-medium text-gray-900 mb-1">
            {report.project_name}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          {report.project_type && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {report.project_type}
            </span>
          )}
          {report.location_info && (
            <span className="text-xs text-gray-500 flex items-center gap-0.5">
              <MapPin size={11} />
              {report.location_info}
            </span>
          )}
        </div>
      </div>

      {/* Completeness + Complexity */}
      <div className="flex gap-2">
        {completenessPercent !== null && (
          <div className="flex-1 rounded-lg border border-gray-100 p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <FileCheck2 size={13} className="text-emerald-500" />
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                {t("smartSurveyor.researchReport.fields.completeness")}
              </span>
            </div>
            <div className="text-lg font-bold text-gray-900 leading-none mb-1.5">
              {completenessPercent}%
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={clsx(
                  "h-full rounded-full transition-all",
                  completenessPercent >= 80
                    ? "bg-emerald-500"
                    : completenessPercent >= 50
                      ? "bg-amber-400"
                      : "bg-red-400",
                )}
                style={{ width: `${completenessPercent}%` }}
              />
            </div>
          </div>
        )}
        {report.complexity_rating && (
          <div className="flex-1 rounded-lg border border-gray-100 p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Gauge size={13} className="text-purple-500" />
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                {t("smartSurveyor.researchReport.fields.complexity")}
              </span>
            </div>
            <span
              className={clsx(
                "text-xs font-semibold px-2 py-1 rounded-md inline-block",
                COMPLEXITY_COLOR[report.complexity_rating.toLowerCase()] ??
                  "text-gray-600 bg-gray-50",
              )}
            >
              {report.complexity_rating}
            </span>
          </div>
        )}
      </div>

      {/* Stat pills */}
      {statItems.length > 0 && (
        <div className="space-y-1.5">
          {statItems.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-2.5 rounded-lg border border-gray-100 px-3 py-2"
            >
              <div
                className={clsx(
                  "w-7 h-7 rounded-md flex items-center justify-center shrink-0",
                  stat.color,
                )}
              >
                <stat.icon size={14} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-gray-900 leading-tight">
                  {stat.value}
                </div>
                <div className="text-[10px] text-gray-400">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Detail fields */}
      <div className="space-y-3">
        {report.applicable_standards &&
          report.applicable_standards.length > 0 && (
            <div className="flex items-start gap-2">
              <Shield
                size={13}
                className="text-gray-400 mt-0.5 shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                  {t("smartSurveyor.researchReport.fields.standards")}
                </div>
                <div className="text-xs text-gray-700 leading-relaxed">
                  {report.applicable_standards.join(", ")}
                </div>
              </div>
            </div>
          )}

        {report.manufacturers && report.manufacturers.length > 0 && (
          <div className="flex items-start gap-2">
            <Factory size={13} className="text-gray-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                {t("smartSurveyor.researchReport.fields.manufacturers")}
              </div>
              <div className="text-xs text-gray-700 leading-relaxed">
                {report.manufacturers.join(", ")}
              </div>
            </div>
          </div>
        )}

        {report.electrical_scope && (
          <div className="flex items-start gap-2">
            <Plug size={13} className="text-gray-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                {t("smartSurveyor.researchReport.fields.scope")}
              </div>
              <div className="text-xs text-gray-700 leading-relaxed">
                {report.electrical_scope}
              </div>
            </div>
          </div>
        )}

        {report.distribution_boards &&
          report.distribution_boards.length > 0 && (
            <div className="flex items-start gap-2">
              <Building2
                size={13}
                className="text-gray-400 mt-0.5 shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-0.5">
                  {t(
                    "smartSurveyor.researchReport.fields.distributionBoards",
                  )}
                </div>
                <div className="text-xs text-gray-700 space-y-0.5">
                  {report.distribution_boards.map((b, i) => (
                    <div key={i} className="leading-relaxed">
                      <span className="font-medium">{b.name}</span>
                      {b.location && (
                        <span className="text-gray-500"> — {b.location}</span>
                      )}
                      {b.estimated_circuits && (
                        <span className="text-gray-400">
                          {" "}
                          (~{b.estimated_circuits})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};
